// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Chip, InputAdornment,
  Paper, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { allHelpEntries } from './helpContent';

// ---------------------------------------------------------------------------
// Primordia — Help Center
// ---------------------------------------------------------------------------
// The browsable wiki behind every HelpBubble. Client-side search over the same
// three content sets HelpBubble reads (SCREEN_HELP / FIELD_HELP / GLOSSARY),
// with three sections plus an "All" view, and deep-linking to one entry: a
// bubble's "Open in wiki" link sets `initialTopic` via CmdbApp AND
// `#help=<topic>` in the URL hash, so this screen finds its target whichever
// one it was mounted from — including a bookmarked or refreshed link, which
// only the hash survives.
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const KIND_LABEL = { screen: 'Screen', field: 'Field', glossary: 'Glossary' };
const KIND_COLOR = { screen: '#0D5B9A', field: '#2E7D32', glossary: '#6A1B9A' };
const TAB_ORDER = ['all', 'screen', 'field', 'glossary'];
const TAB_LABEL = { all: 'All', screen: 'Screens', field: 'Fields', glossary: 'Glossary' };

function readHashTopic() {
  const m = /help=([^&]+)/.exec(window.location.hash || '');
  return m ? decodeURIComponent(m[1]) : '';
}

export default function HelpCenter({ initialTopic }) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [expandedKey, setExpandedKey] = useState('');
  const itemRefs = useRef({});

  const entries = useMemo(() => allHelpEntries(), []);

  const filtered = useMemo(() => {
    const byTab = tab === 'all' ? entries : entries.filter((e) => e.kind === tab);
    const q = search.trim().toLowerCase();
    const bySearch = !q ? byTab : byTab.filter((e) => {
      const hay = [e.title, e.body, ...(e.tips || [])].join(' \n ').toLowerCase();
      return hay.includes(q);
    });
    return bySearch.slice().sort((a, b) => a.title.localeCompare(b.title));
  }, [entries, tab, search]);

  const grouped = useMemo(() => {
    if (tab !== 'all') return [[tab, filtered]];
    const buckets = { screen: [], field: [], glossary: [] };
    filtered.forEach((e) => buckets[e.kind].push(e));
    return [['screen', buckets.screen], ['field', buckets.field], ['glossary', buckets.glossary]];
  }, [filtered, tab]);

  // Deep link — an explicit prop takes priority (fired live via HelpBubble's
  // "Open in wiki" while the app is already running); the URL hash is the
  // fallback so a bookmarked or refreshed link still lands on the right entry.
  useEffect(() => {
    const topic = initialTopic || readHashTopic();
    if (!topic) return;
    const entry = entries.find((e) => e.key === topic);
    if (!entry) return;
    setSearch('');
    setTab('all');
    const domKey = `${entry.kind}-${entry.key}`;
    setExpandedKey(domKey);
    // Wait for the (now-expanded, now-unfiltered) list to paint before scrolling.
    const t = setTimeout(() => {
      const node = itemRefs.current[domKey];
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTopic, entries]);

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <MenuBookIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Help Center</Typography>
        <Chip size="small" label={`${entries.length} topics`} sx={{ height: 20 }} />
      </Stack>
      <Typography sx={{ fontSize: 13, color: GREY, mb: 2 }}>
        What every screen and field does, and the concepts behind them. Look for the <b>?</b> bubble
        next to a field or screen title anywhere in the app — it links straight back here.
      </Typography>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <TextField
          fullWidth size="small" placeholder="Search screens, fields and concepts…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontWeight: 600, textTransform: 'none' } }}>
          {TAB_ORDER.map((k) => (
            <Tab key={k} value={k} label={`${TAB_LABEL[k]} (${k === 'all' ? entries.length : entries.filter((e) => e.kind === k).length})`} />
          ))}
        </Tabs>
      </Paper>

      {!filtered.length && (
        <Paper sx={{ p: 3, textAlign: 'center', color: GREY }}>
          No help topics match &ldquo;{search}&rdquo;.
        </Paper>
      )}

      {grouped.map(([kind, items]) => (
        items.length > 0 && (
          <Box key={kind} sx={{ mb: 3 }}>
            {tab === 'all' && (
              <Typography variant="overline" sx={{ color: HEAD, fontWeight: 700, letterSpacing: 1 }}>
                {TAB_LABEL[kind]} ({items.length})
              </Typography>
            )}
            <Paper variant="outlined" sx={{ mt: 0.5 }}>
              {items.map((entry) => {
                const domKey = `${entry.kind}-${entry.key}`;
                return (
                  <Accordion
                    key={domKey}
                    disableGutters
                    square
                    expanded={expandedKey === domKey}
                    onChange={(e, isExp) => setExpandedKey(isExp ? domKey : '')}
                    ref={(node) => { itemRefs.current[domKey] = node; }}
                    sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #eee' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip size="small" label={KIND_LABEL[entry.kind]}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#fff', bgcolor: KIND_COLOR[entry.kind] }} />
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{entry.title}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ fontSize: 13.5, color: '#333', lineHeight: 1.6 }}>{entry.body}</Typography>
                      {!!(entry.tips && entry.tips.length) && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                          {entry.tips.map((t) => (
                            <Typography key={t} component="li" sx={{ fontSize: 12.5, color: GREY, mb: 0.4 }}>{t}</Typography>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Paper>
          </Box>
        )
      ))}
    </Box>
  );
}
